// src/whatsappChat/socket/WaSocketProvider.jsx
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { useQueryClient } from '@tanstack/react-query';
import { getAuthToken, notifyWaUnreadChanged, WA_CHAT_SOCKET_URL } from '../api/client';
import { applyStatuses, markConversationRead, mergeMessages, patchThread, upsertConversation } from '../api/cache';

const WaSocketContext = createContext({ connected: false, emit: () => {} });

export const useWaSocket = () => useContext(WaSocketContext);

/**
 * One socket for the whole module, on the /botsense-chat namespace.
 * Server events are written straight into the react-query cache, so screens
 * only read from the cache. `connected` is true between `ready` and a drop;
 * while it is false the REST hooks fall back to polling.
 */
export function WaSocketProvider({ enabled, children }) {
  const qc = useQueryClient();
  const socketRef = useRef(null);
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    if (!enabled || !getAuthToken()) return undefined;

    const socket = io(WA_CHAT_SOCKET_URL, {
      // Read at every (re)connect so a refreshed login is picked up.
      auth: (cb) => cb({ token: getAuthToken() }),
      transports: ['websocket'],
    });
    socketRef.current = socket;
    let wasReady = false;

    socket.on('ready', () => {
      // Nothing missed during a drop is replayed: reload the inbox. The open
      // chat re-sends chat:open with its last_id when `connected` flips back.
      if (wasReady) qc.invalidateQueries({ queryKey: ['wa-conversations'] });
      wasReady = true;
      setConnected(true);
    });

    socket.on('disconnect', () => setConnected(false));

    socket.on('unauthorized', () => {
      // Bad token or no chat right: stop, do not keep reconnecting.
      socket.disconnect();
      setConnected(false);
    });

    socket.on('conversation:update', (row) => {
      if (!row || row.id == null) return;
      upsertConversation(qc, row);
      notifyWaUnreadChanged();

      const openThread = qc.getQueryData(['wa-thread', Number(row.id)]);
      if (openThread) {
        if (typeof row.window_open === 'boolean') {
          patchThread(qc, row.id, () => ({ windowOpen: row.window_open }));
        }
        // The chat is on screen, so what just arrived has been seen.
        if (row.unread_count > 0) markConversationRead(qc, Number(row.id));
      }
    });

    socket.on('message:new', (payload) => {
      const messages = Array.isArray(payload?.messages) ? payload.messages : [];
      if (!payload?.conversation_id || messages.length === 0) return;
      notifyWaUnreadChanged();
      patchThread(qc, payload.conversation_id, (prev) => ({
        messages: mergeMessages(prev.messages, messages),
      }));
    });

    socket.on('message:status', (payload) => {
      const messages = Array.isArray(payload?.messages) ? payload.messages : [];
      if (!payload?.conversation_id || messages.length === 0) return;
      patchThread(qc, payload.conversation_id, (prev) => ({
        messages: applyStatuses(prev.messages, messages),
      }));
    });

    socket.on('chat:closed', (payload) => {
      if (!payload?.conversation_id) return;
      patchThread(qc, payload.conversation_id, () => ({ accessLost: true }));
    });

    return () => {
      socket.removeAllListeners();
      socket.disconnect();
      socketRef.current = null;
      setConnected(false);
    };
  }, [enabled, qc]);

  const emit = useCallback((event, payload, ack) => {
    const socket = socketRef.current;
    if (!socket || !socket.connected) return false;
    if (ack) socket.emit(event, payload, ack);
    else socket.emit(event, payload);
    return true;
  }, []);

  const value = useMemo(() => ({ connected, emit }), [connected, emit]);

  return <WaSocketContext.Provider value={value}>{children}</WaSocketContext.Provider>;
}
